import { computed } from "vue";
import { toBoolean } from "#ce/utils/props";

/**
 * The classes and resolved appearance the root, the picker and the activator render with.
 *
 * The text appearance sizes its activator to the selected label, so its menu takes the `inline`
 * appearance, which sizes the popup itself. It also defaults the activator to the borderless
 * `plain` variant and `compact` density; an explicitly chosen variant or density still wins.
 *
 * @param {object} props  Owning component's reactive props; reads `appearance`, `variant`,
 *   `density` and `wrap`.
 * @param {{ disabled: import("vue").Ref<boolean>, menuShown: import("vue").Ref<boolean> }} options
 * @returns {{ rootClass: import("vue").ComputedRef<object>, modifierClass: import("vue").ComputedRef<object>,
 *   menuAppearance: import("vue").ComputedRef<string>, activatorVariant: import("vue").ComputedRef<string>,
 *   activatorDensity: import("vue").ComputedRef<string> }}
 */
export function useSelectAppearance(props, { disabled, menuShown }) {
  const textAppearance = computed(() => props.appearance === "text");
  const activatorDensity = computed(() =>
    textAppearance.value && props.density === "default" ? "compact" : props.density,
  );

  const rootClass = computed(() => ({
    "--wrap": toBoolean(props.wrap),
    "--is-active": menuShown.value,
    "--appearance-text": textAppearance.value,
    "--density-compact": activatorDensity.value === "compact",
  }));

  const modifierClass = computed(() => ({
    "--disabled": disabled.value,
    "--is-active": menuShown.value,
  }));

  const menuAppearance = computed(() => (textAppearance.value ? "inline" : props.appearance));

  const activatorVariant = computed(() =>
    textAppearance.value && props.variant === "default" ? "plain" : props.variant,
  );

  return {
    rootClass,
    modifierClass,
    menuAppearance,
    activatorVariant,
    activatorDensity,
  };
}
